import { unwrapApiResponse } from '../../../api/responseNormalizer';
import { readPtpCall } from '../../vessel-master';
import type {
  PtpCallChange,
  VesselAlert,
  VesselAlertKind,
  VesselChangeGroup,
  VesselChangeJob,
  VesselChangeList,
} from '../types/vesselChanges';

type JsonRecord = Record<string, unknown>;

const asRecord = (value: unknown): JsonRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const text = (value: unknown): string | null => (value === null || value === undefined || value === '' ? null : String(value));
const id = (value: unknown): number | null => (value === null || value === undefined || value === '' ? null : Number(value));

const readChange = (raw: unknown): PtpCallChange => {
  const row = asRecord(raw);
  return { field: String(row.field ?? ''), oldValue: text(row.oldValue), newValue: text(row.newValue), seenAt: text(row.seenAt) };
};

export const readVesselChangeJob = (raw: unknown): VesselChangeJob => {
  const row = asRecord(raw);
  return {
    saleOrderId: id(row.saleOrderId) ?? 0,
    jobNo: String(row.jobNo ?? ''),
    customerName: String(row.customerName ?? ''),
    employeeId: id(row.employeeId),
    employeeName: String(row.employeeName ?? ''),
    leg: row.leg === 'OFF' ? 'OFF' : 'LOADING',
    vesselName: String(row.vesselName ?? ''),
    currentEta: text(row.currentEta),
    currentEtd: text(row.currentEtd),
    currentScn: text(row.currentScn),
    ptpEta: text(row.ptpEta),
    ptpEtd: text(row.ptpEtd),
    ptpScn: text(row.ptpScn),
    etaDiffers: row.etaDiffers === true,
    etdDiffers: row.etdDiffers === true,
    scnDiffers: row.scnDiffers === true,
    boardingMonthFrom: text(row.boardingMonthFrom),
    boardingMonthTo: text(row.boardingMonthTo),
    port: text(row.port),
    terminal: text(row.terminal),
  };
};

const readGroup = (raw: unknown): VesselChangeGroup => {
  const row = asRecord(raw);
  return {
    call: readPtpCall(row.call),
    lastChangedAt: text(row.lastChangedAt),
    changes: asList(row.changes).map(readChange),
    jobs: asList(row.jobs).map(readVesselChangeJob),
  };
};

export const readVesselChangeList = (body: unknown): VesselChangeList => {
  const row = asRecord(unwrapApiResponse<unknown>(body));
  const byPort = asRecord(row.lastReadAtByPort);
  return {
    lastGoodReadAt: text(row.lastGoodReadAt),
    lastReadAtByPort: { PTP: text(byPort.PTP), WESTPORTS: text(byPort.WESTPORTS), MMSW: text(byPort.MMSW) },
    groups: asList(row.groups).map(readGroup),
  };
};

const ALERT_KINDS: VesselAlertKind[] = ['ETA', 'LINK', 'OMITTED'];

const readVesselAlert = (raw: unknown): VesselAlert => {
  const row = asRecord(raw);
  return {
    id: id(row.id) ?? 0,
    kind: ALERT_KINDS.find((kind) => kind === row.kind) ?? 'ETA',
    ptpVesselCallId: id(row.ptpVesselCallId),
    vesselName: String(row.vesselName ?? ''),
    voyage: text(row.voyage),
    arriveAt: text(row.arriveAt),
    arriveEstimated: row.arriveEstimated === true,
    departAt: text(row.departAt),
    departEstimated: row.departEstimated === true,
    jobs: id(row.jobs) ?? 0,
    employeeId: id(row.employeeId),
    employeeName: String(row.employeeName ?? ''),
    mine: row.mine === true,
    sentAt: text(row.sentAt),
    reminders: id(row.reminders) ?? 0,
    escalated: row.escalated === true,
    saleOrderId: id(row.saleOrderId),
    jobNo: text(row.jobNo),
    unassigned: row.unassigned === true,
    port: text(row.port),
    portLabel: text(row.portLabel),
    terminal: text(row.terminal),
  };
};

/** The server's order (mine first, newest first) is kept. */
export const readVesselAlerts = (body: unknown): VesselAlert[] =>
  asList(unwrapApiResponse<unknown>(body)).map(readVesselAlert);
